import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { SiteInfoService } from '@api/site-info.service';
import { SiteInfoDTO } from '@dto/accounts/models';
import { BaseObject } from '@shared/base/base-object';
import { isNotNull } from '@shared/base/core';
import { PasskeyOfferService } from '@shared/helpers/passkey-offer.service';
import { RerdirectService } from '@shared/helpers/redirect.service';
import { LayoutState } from 'app/layout/layout.state';
import { NotificationsService } from 'app/notifications/notifications.service';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'app-layout',
  },
  standalone: false,
})
export class LayoutComponent extends BaseObject implements OnInit {
  public readonly SiteInfoType = SiteInfoDTO.TypeEnum;

  public copyrightData$ = this.siteInfoService.getByType('COPYRIGHT');
  public needSubscription: boolean;

  // для отключения скролла после клика средней кнопкой мыши
  @HostListener('mousedown', ['$event'])
  public _onMousedown(e: MouseEvent): boolean {
    if (e.button === 1) {
      return false;
    } else {
      return true;
    }
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public layoutState: LayoutState,
    private siteInfoService: SiteInfoService,
    private notificationsService: NotificationsService,
    private rerdirectService: RerdirectService,
    private passkeyOfferService: PasskeyOfferService,
  ) {
    super();

    this.notificationsService.createPushNotificationsContainer();
    this.notificationsService.subscribeToPushNotifications();
  }

  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.changeOptions());

    this.changeOptions();
  }

  private changeOptions(): void {
    const currentRoute = this.getLastChild();

    currentRoute.data.pipe(take(1)).subscribe((data) => {
      this.notificationsService.openCookieAcceptDialog();
      // The shell is where every way in ends up — password, 2FA, passkey and SSO alike — so it is
      // the only place a "you have just signed in" offer can be hung. Both calls latch internally;
      // this one decides from the token's `auth_time` and keeps its own once-per-authentication
      // marker, so the reload each login redirect causes neither hides the offer nor repeats it
      // (see PasskeyOfferService).
      this.passkeyOfferService.offerIfNeeded();

      if (data.isLeafNode) {
        const parentData = currentRoute.parent.snapshot.data;

        this.layoutState.showNotifications$.next(
          isNotNull(parentData.showNotifications) ? parentData.showNotifications : true,
        );
      }
    });
  }

  private getLastChild(): ActivatedRoute {
    let lastChild = this.activatedRoute.firstChild;

    while (lastChild.children && lastChild.children.length > 0) {
      lastChild = lastChild.firstChild;
    }

    return lastChild;
  }

  public _goToTermsOfUse(): void {
    this.rerdirectService.goToTermsOfUse();
  }

  public _onMouseupToTermsOfUse(e: MouseEvent): void {
    if (e.button === 1) {
      this.rerdirectService.goToTermsOfUse();
    }
  }

  public _goToCookiePolicy(): void {
    this.rerdirectService.goToCookiePolicy();
  }

  public _onMouseupToCookiePolicy(e: MouseEvent): void {
    if (e.button === 1) {
      this.rerdirectService.goToCookiePolicy();
    }
  }

  public _goToPrivacyPolicy(): void {
    this.rerdirectService.goToPrivacyPolicy();
  }

  public _onMouseupToPrivacyPolicy(e: MouseEvent): void {
    if (e.button === 1) {
      this.rerdirectService.goToPrivacyPolicy();
    }
  }
}
